import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { MapPin, LocateFixed, Trash2, Search, ExternalLink, ImageIcon, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Tooltip } from "@mui/material";
import Modal from '../../components/common/CommonModal';
import CreditCustomerList from '../../components/common/CreditCustomerList';

const mapApiToForm = (api) => ({
    buildingId: api.BuildingID || 0,
    build_key: api.BuildingCode || '',
    build_name: api.BuildingName || '',
    projectCommunity: api.ProjectID || '',
    blockName: api.BlockName || '',
    propertyType: api.PropertyType || '',
    buildingCategory: api.Category || '',
    status: api.BuildingStatus || '',
    landlord: api.OwnerID || '',
    developer: api.Developer || '',
    numFloors: api.NoOfFloors || '',
    numUnits: api.NoOfUnits || '',
    numParking: api.NoOfParking || '',
    numElevators: api.NoOfElevators || '',
    yearBuilt: api.YearBuilt || '',
    plotNo: api.PlotNo || '',
    landArea: api.LandArea || '',
    builtUpArea: api.BuiltUpArea || '',
    totalRentalArea: api.RentableArea || '',
    commonArea: api.CommonArea || '',
    areaUnit: api.AreaUnit || 'Sq.Ft',
    address: api.Address1 || '',
    city: api.City || '',
    state: api.State || '',
    country: api.Country || '',
    postalCode: api.PostalCode || '',
    makaniNumber: api.MakaniNo || '',
    municipalityNo: api.MunicipalityNo || '',
    reraPropertyNo: api.ReraPropertyNo || '',
    latitude: api.Latitude || 0,
    longitude: api.Longitude || 0,
    buildingManager: api.ManagerName || '',
    contactNumber: api.MobileNo || '',
    telephone: api.Telephone || '',
    email: api.Email || '',
    maintenanceCompany: api.MaintenanceCompany || '',
    securityCompany: api.SecurityCompany || '',
    // othersIncome:api.RentalIncomeAccount || '',
    // othersPrepaid:api.RentalIncomeAccount || '',
    rentIncome: api.RentalIncomeAccount || '',
    rentPrepaid: api.RentalIncomeAccountPP || '',
    serviceIncome: api.MaintenanceIncomeAccount || '',
    servicePrepaid: api.MaintenanceIncomeAccountPP || '',
    ejariIncome: api.EjariIncomeAccount || '',
    ejariPrepaid: api.EjariIncomeAccountPP || '',
    commissionIncome: api.CommissionIncomeAccount || '',
    commissionPrepaid: api.CommissionIncomeAccountPP || '',
    parkingIncome: api.ParkingIncomeAccount || '',
    parkingPrepaid: api.ParkingIncomeAccountPP || '',
    depositAcc: api.DepositAccount || '',
    expenseAcc: api.ExpenseAccount || '',
    settlementAcc: api.SettlementAccount || '',
    cashAcc: api.CashAccount || '',
    pdcAcc: api.PDCAccount || '',
    vatOutputAcc: api.VATOutputAccount || '',
    vatInputAcc: api.VATInputAccount || '',
    costCenter: api.CostCenter || '',
    electricityProvider: api.ElectricityProvider || '',
    waterProvider: api.WaterProvider || '',
    gasProvider: api.GasProvider || '',
    insuranceDetails: api.InsuranceCompany || '',
    policyNumber: api.PolicyNumber || '',
    policyExpiryDate: api.PolicyExpiryDate || '',
    hasGym: api.HasGym || false,
    hasSwimmingPool: api.HasSwimmingPool || false,
    hasKidsPlayArea: api.HasKidsPlayArea || false,
    hasCCTV: api.HasCCTV || false,
    hasCentralAC: api.HasCentralAC || false,
    hasFireAlarm: api.HasFireAlarm || false,
    hasFireFighting: api.HasFireFighting || false,
    hasGenerator: api.HasGenerator || false,
    hasVisitorParking: api.HasVisitorParking || false,
    notes: api.Remarks || '',
    rentMode: 'Monthly',
    geoLocation: (api.Latitude && api.Longitude) ? { lat: api.Latitude, lng: api.Longitude } : null,
    gpsCoordinates: (api.Latitude && api.Longitude) ? `${api.Latitude}, ${api.Longitude}` : '',
});

const emptyFormState = {
    build_key: "", build_name: "", plotNo: "", location: "", department: "R&K PROPERTIES",
    ownerName: "", developer: "", propertyType: "Residential", projectCommunity: "",
    numFloors: "", numUnits: "", numParking: "", numElevators: "",
    yearBuilt: "", status: "Active", buildingCategory: "Freehold",
    address: "", city: "", state: "", country: "UAE", postalCode: "",
    gpsCoordinates: "", municipalityNo: "", makaniNumber: "",
    landArea: "", builtUpArea: "", totalRentalArea: "", commonArea: "",
    facilities: "", maintenanceCompany: "", securityCompany: "",
    insuranceDetails: "", utilityProviders: "",
    buildingManager: "", contactNumber: "", email: "",
    latitude: "",
    longitude: "",
    notes: "",
    landlord: "", landlordAddress: "", landlordPhone: "", landlordEmail: "",
    depositAcc: "", settlementAcc: "", cashAcc: "", pdcAcc: "", vatOutputAcc: "", vatInputAcc: "", ejariFixedAmt: "", ejariAcc: "", commissionAcc: "",
    rentIncome: "", rentPrepaid: "", serviceIncome: "", servicePrepaid: "", ejariIncome: "", ejariPrepaid: "",
    commissionIncome: "", commissionPrepaid: "", parkingIncome: "", parkingPrepaid: "", othersIncome: "", othersPrepaid: "",
    rentMode: "Monthly",
    geoLocation: null,
};

export default function BuildingRegister() {
    const [accounts, setAccounts] = useState([]);
    const [activeTab, setActiveTab] = useState('building_info'); // 'building_info' | 'account_setting'
    const [buildings, setBuildings] = useState([]);
    const [loadingBuildings, setLoadingBuildings] = useState(true);
    const [formData, setFormData] = useState(emptyFormState);
    const [searchQuery, setSearchQuery] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [imageData, setImageData] = useState(null);
    const [imageViewUrl, setImageViewUrl] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [snackbarQueue, setSnackbarQueue] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [buildingThumbnails, setBuildingThumbnails] = useState({});
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState('');
    const [addressInput, setAddressInput] = useState('');
    const [geocoding, setGeocoding] = useState(false);
    const [loading, setLoading] = useState(false);
    const [depositAccount, setDepositAccount] = useState("");
    const [depositModalOpen, setDepositModalOpen] = useState(false);
    const [settlementAccount, setSettlementAccount] = useState("");
    const [settlementModalOpen, setSettlementModalOpen] = useState(false);
    const [cashAccount, setCashAccount] = useState("");
    const [cashModalOpen, setCashModalOpen] = useState(false);
    const [pdcAccount, setPdcAccount] = useState("");
    const [pdcModalOpen, setPdcModalOpen] = useState(false);
    const [vatOutputAccount, setVatOutputAccount] = useState("");
    const [vatOutputModalOpen, setVatOutputModalOpen] = useState(false);
    const [vatInputAccount, setVatInputAccount] = useState("");
    const [vatInputModalOpen, setVatInputModalOpen] = useState(false);
    const [ejariAccount, setEjariAccount] = useState("");
    const [ejariModalOpen, setEjariModalOpen] = useState(false);
    const [commissionAccount, setCommissionAccount] = useState("");
    const [commissionModalOpen, setCommissionModalOpen] = useState(false);
    const [activeModalField, setActiveModalField] = useState(null);

    // Income Account field states
    const [rentIncomeAccount, setRentIncomeAccount] = useState("");
    const [rentPrepaidAccount, setRentPrepaidAccount] = useState("");
    const [serviceIncomeAccount, setServiceIncomeAccount] = useState("");
    const [servicePrepaidAccount, setServicePrepaidAccount] = useState("");
    const [ejariIncomeAccount, setEjariIncomeAccount] = useState("");
    const [ejariPrepaidAccount, setEjariPrepaidAccount] = useState("");
    const [commissionIncomeAccount, setCommissionIncomeAccount] = useState("");
    const [commissionPrepaidAccount, setCommissionPrepaidAccount] = useState("");
    const [parkingIncomeAccount, setParkingIncomeAccount] = useState("");
    const [parkingPrepaidAccount, setParkingPrepaidAccount] = useState("");
    const [othersIncomeAccount, setOthersIncomeAccount] = useState("");
    const [othersPrepaidAccount, setOthersPrepaidAccount] = useState("");

    // Income Account modal states
    const [rentIncomeModalOpen, setRentIncomeModalOpen] = useState(false);
    const [rentPrepaidModalOpen, setRentPrepaidModalOpen] = useState(false);
    const [serviceIncomeModalOpen, setServiceIncomeModalOpen] = useState(false);
    const [servicePrepaidModalOpen, setServicePrepaidModalOpen] = useState(false);
    const [ejariIncomeModalOpen, setEjariIncomeModalOpen] = useState(false);
    const [ejariPrepaidModalOpen, setEjariPrepaidModalOpen] = useState(false);
    const [commissionIncomeModalOpen, setCommissionIncomeModalOpen] = useState(false);
    const [commissionPrepaidModalOpen, setCommissionPrepaidModalOpen] = useState(false);
    const [parkingIncomeModalOpen, setParkingIncomeModalOpen] = useState(false);
    const [parkingPrepaidModalOpen, setParkingPrepaidModalOpen] = useState(false);
    const [othersIncomeModalOpen, setOthersIncomeModalOpen] = useState(false);
    const [othersPrepaidModalOpen, setOthersPrepaidModalOpen] = useState(false);

    useEffect(() => {
        fetchAccountList("ALL");
    }, []);


    const fetchAccountList = async (valueToAPI) => {
        setLoading(true);
        let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
        let companyCode = localStorage.getItem("companyCode") || "-";
        let department = localStorage.getItem("currentSelectedDepartment") || "-";


        let apiUrl = `${apiPaths[0]?.API_CONFIG}/api/CRMAccountList/${companyCode}/ACCOUNTLIST/${department}/${valueToAPI}/-/-`;

        try {
            const response = await axios.get(apiUrl);
            setAccounts(response.data || []);
        } catch (err) {
            console.error("Error while fetching voucher accounts", err);
        } finally {
            setLoading(false);
        }
    };


    const clearAccountField = (fieldId) => {
        const setterMap = {
            depositAcc: setDepositAccount,
            settlementAcc: setSettlementAccount,
            cashAcc: setCashAccount,
            pdcAcc: setPdcAccount,
            vatOutputAcc: setVatOutputAccount,
            vatInputAcc: setVatInputAccount,
            ejariAcc: setEjariAccount,
            commissionAcc: setCommissionAccount,
            rentIncome: setRentIncomeAccount,
            rentPrepaid: setRentPrepaidAccount,
            serviceIncome: setServiceIncomeAccount,
            servicePrepaid: setServicePrepaidAccount,
            ejariIncome: setEjariIncomeAccount,
            ejariPrepaid: setEjariPrepaidAccount,
            commissionIncome: setCommissionIncomeAccount,
            commissionPrepaid: setCommissionPrepaidAccount,
            parkingIncome: setParkingIncomeAccount,
            parkingPrepaid: setParkingPrepaidAccount,
            othersIncome: setOthersIncomeAccount,
            othersPrepaid: setOthersPrepaidAccount,
        };
        setterMap[fieldId]("");
        setFormData((prev) => ({ ...prev, [fieldId]: "" }));
    };

    useEffect(() => {
        let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
        setImageViewUrl(apiPaths[0]?.IMG_API_PATH);
        const fetchBuildings = async () => {

            let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
            let companyCode = localStorage.getItem("companyCode") || "-";
            let department = localStorage.getItem("currentSelectedDepartment") || "-";
            try {
                const res = await axios.get(`${apiPaths[0]?.API_CONFIG}/api/CRMTaskMainListFilter/${companyCode}/BUILDING/-/-/-/-/-/1900-01-01/1900-01-01/-/-/-`);
                const data = Array.isArray(res.data) ? res.data : [];
                setBuildings(data.map(mapApiToForm));
            } catch (err) {
                console.error('Failed to fetch buildings:', err);
            } finally {
                setLoadingBuildings(false);
            }
        };
        fetchBuildings();
    }, []);


    useEffect(() => {
        if (buildings.length === 0 || !imageViewUrl) return;

        const fetchThumbnails = async () => {
            let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
            let companyCode = localStorage.getItem("companyCode") || "-";
            const thumbnails = {};

            for (const building of buildings) {
                try {
                    const url = `${apiPaths[0]?.API_CONFIG}/api/CpaysCount/${companyCode}/DOCIMAGE/${building.build_key}/BUILDINGIMAGE`;
                    const response = await axios.get(url);
                    if (response.data && response.data.length > 0) {
                        const firstImage = response.data[0];
                        const relativePath = firstImage.Imagepath.split("BONDTIME_REALESTATE_IMG")[1];
                        const normalizedPath = ("\\BONDTIME_REALESTATE_IMG\\" + relativePath).replace(/\\/g, "/");
                        const splitKey = "BONDTIME_REALESTATE_IMG/";
                        const extractedFileName = normalizedPath.includes(splitKey)
                            ? normalizedPath.split(splitKey)[1]
                            : normalizedPath.split("/").pop();
                        if (extractedFileName) {
                            const cleanBase = imageViewUrl?.replace(/\/$/, "") || "";
                            thumbnails[building.build_key] = `${cleanBase}/BONDTIME_REALESTATE_IMG/${extractedFileName}`;
                        }
                    }
                } catch (error) {
                    // silently skip failed thumbnail fetches
                }
            }
            setBuildingThumbnails(thumbnails);
        };

        fetchThumbnails();
    }, [buildings, imageViewUrl]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleFacilityToggle = (facility) => {
        setFormData(prev => {
            const facilities = prev.facilities ? prev.facilities.split(',').map(f => f.trim()).filter(Boolean) : [];
            const exists = facilities.includes(facility);
            const updated = exists
                ? facilities.filter(f => f !== facility)
                : [...facilities, facility];
            return { ...prev, facilities: updated.join(', ') };
        });
    };

    const handlePrev = () => {
        setCurrentImageIndex((prev) =>
            prev > 0 ? prev - 1 : (imageData?.length || 1) - 1,
        );
    };

    const handleNext = () => {
        setCurrentImageIndex((prev) =>
            prev < (imageData?.length || 1) - 1 ? prev + 1 : 0,
        );
    };

    /* ---------------- Server-side Image Functions ---------------- */

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const queueSnackbar = (message, severity = "success") => {
        setSnackbarQueue(prev => [...prev, { message, severity }]);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
        if (snackbarQueue.length > 0) {
            setTimeout(() => {
                const [next, ...rest] = snackbarQueue;
                setSnackbarQueue(rest);
                setSnackbar({ open: true, message: next.message, severity: next.severity });
            }, 300);
        }
    };

    const buildImageUrl = (baseUrl, imagePath) => {
        if (!imagePath) return "";
        return (
            baseUrl?.replace(/\/+$/, "") +
            "/" +
            imagePath
                ?.replace(/\\/g, "/")
                ?.replace(/^.*BONDTIME_REALESTATE_IMG\//, "BONDTIME_REALESTATE_IMG")
                ?.replace(/^\/+/, "")
        );
    };

    const getImageUrl = (itemFilePath) => {
        if (!itemFilePath || typeof itemFilePath !== "string") return "";
        const normalizedPath = itemFilePath.replace(/\\/g, "/");
        const splitKey = "BONDTIME_REALESTATE_IMG/";
        const extractedFileName = normalizedPath.includes(splitKey)
            ? normalizedPath.split(splitKey)[1]
            : normalizedPath.split("/").pop();
        if (!extractedFileName) return "";
        const cleanBase = imageViewUrl?.replace(/\/$/, "") || "";
        return `${cleanBase}/BONDTIME_REALESTATE_IMG/${extractedFileName}`;
    };

    const fetchImage = async (code) => {
        let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
        let companyCode = localStorage.getItem("companyCode") || "-";
        const url = `${apiPaths[0]?.API_CONFIG}/api/CpaysCount/${companyCode}/DOCIMAGE/${code}/BUILDINGIMAGE`;

        try {
            const response = await axios.get(url);
            const imgs = (response.data || []).map((item) => {
                const relativePath = item.Imagepath.split("BONDTIME_REALESTATE_IMG")[1];
                return {
                    slno: item.slno,
                    imagePath: "\\BONDTIME_REALESTATE_IMG\\" + relativePath,
                };
            });
            setImageData(imgs);
            setCurrentImageIndex(0);
        } catch (error) {
            console.error("Error fetching building image:", error);
            setImageData([]);
        }
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    const updateItemImage = async (companyCode, buildingCode) => {
        try {
            const apiPaths = JSON.parse(localStorage.getItem("apiPaths")) || [];
            const BASE_URL = apiPaths[0]?.API_CONFIG;
            const response = await axios.get(
                `${BASE_URL}/api/CRMItemImageUpdate/${companyCode}/${buildingCode}`
            );
            console.log("Building image update success:", response.data);
            return response.data;
        } catch (error) {
            console.error("Building image update failed:", error);
            throw error;
        }
    };

    const uploadImage = async (selectedFile, buildingCode) => {
        if (!selectedFile) {
            throw new Error("No file selected");
        }

        const companyCode = localStorage.getItem("companyCode") || "-";
        const apiPaths = JSON.parse(localStorage.getItem("apiPaths")) || [];
        const base64String = await fileToBase64(selectedFile);

        const fd = new FormData();
        fd.append("DOC_CODE", buildingCode || "");
        fd.append("DOC_TYPE", "BUILDINGIMAGE");
        fd.append("file", selectedFile);
        fd.append("cmpcode", companyCode);
        fd.append("IMGBASE64", base64String);
        fd.append(
            "IMAGEPATH",
            `${apiPaths[0].IMG_SERVERPATH}\\BONDTIME_REALESTATE_IMG`
        );
        fd.append("description", "test");

        const response = await axios.post(
            `${apiPaths[0].IMG_POST_PATH}/api/Image/upload`,
            fd,
            {
                headers: { "Content-Type": "multipart/form-data" },
            }
        );

        console.log("Building Image Upload Success:", response.data);
        await updateItemImage(companyCode, buildingCode);

        return response.data;
    };

    const handleAddImage = () => {
        const buildingCode = formData.build_key;
        if (!buildingCode) {
            showSnackbar("Please enter a Building Code before uploading images.", "error");
            return;
        }

        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.multiple = true;

        input.onchange = async (e) => {
            const files = Array.from(e.target.files);
            if (files.length === 0) return;

            setUploadingImages(true);
            let successCount = 0;
            let failCount = 0;

            for (const file of files) {
                try {
                    await uploadImage(file, buildingCode);
                    successCount++;
                } catch (error) {
                    console.error("Image upload failed:", error);
                    failCount++;
                }
            }

            await fetchImage(buildingCode);
            setUploadingImages(false);

            if (successCount > 0) {
                showSnackbar(`${successCount} image(s) uploaded successfully!`, "success");
            }
            if (failCount > 0) {
                queueSnackbar(`${failCount} image(s) failed to upload.`, "error");
            }
        };

        input.click();
    };

    const handleRemoveItem = async () => {
        if (!imageData?.length) return;

        const currentImage = imageData[currentImageIndex];
        if (!currentImage?.slno) return;

        let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
        let companyCode = localStorage.getItem("companyCode") || "-";
        let currentUserName = localStorage.getItem("currentUserName");

        try {
            const url = `${apiPaths[0]?.API_CONFIG}/api/CRMImageDelete`;
            const payload = [
                {
                    CmpCode: companyCode,
                    mode: "DELETE",
                    docuId: currentImage.slno,
                    comments: "Image removed",
                    user: currentUserName,
                },
            ];

            await axios.post(url, payload);

            showSnackbar("Image removed successfully", "success");

            setImageData((prev) =>
                prev.filter((img) => img.slno !== currentImage.slno)
            );
            setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } catch (error) {
            console.error("Failed to delete image:", error);
            showSnackbar("Failed to remove image", "error");
        }
    };

    /* ---------------- Location ---------------- */

    const useCurrentLocation = () => {
        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by this browser.');
            return;
        }
        setLocating(true);
        setLocationError('');
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setFormData(prev => ({
                    ...prev,
                    geoLocation: {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                    },
                    latitude: pos.coords.latitude.toFixed(5),
                    longitude: pos.coords.longitude.toFixed(5),
                    gpsCoordinates: `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`,
                }));
                setLocating(false);
            },
            (err) => {
                setLocationError(err.message || 'Unable to fetch location.');
                setLocating(false);
            }
        );
    };

    const clearLocation = () => {
        setFormData(prev => ({ ...prev, geoLocation: null, gpsCoordinates: '', latitude: '', longitude: '' }));
        setAddressInput('');
    };

    const searchAddress = async () => {
        if (!addressInput.trim()) return;
        setGeocoding(true);
        setLocationError('');
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addressInput)}`
            );
            const data = await res.json();
            if (data && data.length > 0) {
                const { lat, lon, display_name } = data[0];
                setFormData(prev => ({
                    ...prev,
                    geoLocation: { lat: parseFloat(lat), lng: parseFloat(lon), address: display_name },
                    latitude: parseFloat(lat).toFixed(5),
                    longitude: parseFloat(lon).toFixed(5),
                    gpsCoordinates: `${parseFloat(lat).toFixed(5)}, ${parseFloat(lon).toFixed(5)}`,
                }));
            } else {
                setLocationError('No results found for that address.');
            }
        } catch (err) {
            setLocationError('Failed to search address. Please try again.');
        } finally {
            setGeocoding(false);
        }
    };

    const loc = formData.geoLocation;
    const mapEmbedUrl = loc
        ? `https://www.openstreetmap.org/export/embed.html?bbox=${loc.lng - 0.004}%2C${loc.lat - 0.003}%2C${loc.lng + 0.004}%2C${loc.lat + 0.003}&layer=mapnik&marker=${loc.lat}%2C${loc.lng}`
        : null;
    const googleMapsUrl = loc ? `https://www.google.com/maps?q=${loc.lat},${loc.lng}` : null;

    const handleSelectRow = (building) => {
        setFormData(building);
        setIsEditing(true);
        fetchImage(building.build_key);

        setDepositAccount(building.depositAcc || "");
        setSettlementAccount(building.settlementAcc || "");
        setCashAccount(building.cashAcc || "");
        setPdcAccount(building.pdcAcc || "");
        setVatOutputAccount(building.vatOutputAcc || "");
        setVatInputAccount(building.vatInputAcc || "");
        setEjariAccount(building.ejariAcc || "");
        setCommissionAccount(building.commissionAcc || "");
        setRentIncomeAccount(building.rentIncome || "");
        setRentPrepaidAccount(building.rentPrepaid || "");
        setServiceIncomeAccount(building.serviceIncome || "");
        setServicePrepaidAccount(building.servicePrepaid || "");
        setEjariIncomeAccount(building.ejariIncome || "");
        setEjariPrepaidAccount(building.ejariPrepaid || "");
        setCommissionIncomeAccount(building.commissionIncome || "");
        setCommissionPrepaidAccount(building.commissionPrepaid || "");
        setParkingIncomeAccount(building.parkingIncome || "");
        setParkingPrepaidAccount(building.parkingPrepaid || "");
        setOthersIncomeAccount(building.othersIncome || "");
        setOthersPrepaidAccount(building.othersPrepaid || "");
    };

    const handleCreateNew = () => {
        setFormData(emptyFormState);
        setIsEditing(false);
        setImageData(null);
        setCurrentImageIndex(0);
        setUploadingImages(false);

        setDepositAccount("");
        setSettlementAccount("");
        setCashAccount("");
        setPdcAccount("");
        setVatOutputAccount("");
        setVatInputAccount("");
        setEjariAccount("");
        setCommissionAccount("");
        setRentIncomeAccount("");
        setRentPrepaidAccount("");
        setServiceIncomeAccount("");
        setServicePrepaidAccount("");
        setEjariIncomeAccount("");
        setEjariPrepaidAccount("");
        setCommissionIncomeAccount("");
        setCommissionPrepaidAccount("");
        setParkingIncomeAccount("");
        setParkingPrepaidAccount("");
        setOthersIncomeAccount("");
        setOthersPrepaidAccount("");
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.build_key || !formData.build_name) {
            showSnackbar("Building Code and Building Name are mandatory.", "error");
            return;
        }

        const now = new Date().toISOString();

        let apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
        let companyCode = localStorage.getItem("companyCode") || "-";
        let department = localStorage.getItem("currentSelectedDepartment") || "-";

        const buildingCode = formData.build_key;

        // Step 1: Save building first
        const payload = [{
            mode: isEditing ? 'EDIT' : 'ENTRY',
            cmpcode: companyCode,
            BuildingID: isEditing ? (formData.buildingId || 0) : 0,
            BuildingCode: formData.build_key || '',
            BuildingName: formData.build_name || '',
            ProjectID: formData.projectCommunity || '',
            BlockName: formData.blockName || '',
            PropertyType: formData.propertyType || '',
            Category: "Premium",
            BuildingStatus: "Active",
            OwnerID: formData.landlord || '',
            Developer: formData.developer || '',
            NoOfFloors: Number(formData.numFloors) || 0,
            NoOfUnits: Number(formData.numUnits) || 0,
            NoOfParking: 0,
            NoOfElevators: Number(formData.numElevators) || 0,
            YearBuilt: formData.yearBuilt || '',
            PlotNo: formData.plotNo || '',
            LandArea: Number(formData.landArea) || 0,
            BuiltUpArea: Number(formData.builtUpArea) || 0,
            RentableArea: Number(formData.totalRentalArea) || 0,
            CommonArea: Number(formData.commonArea) || 0,
            AreaUnit: 'Sq.Ft',
            Address1: formData.address || '',
            Address2: '',
            City: formData.city || '',
            State: formData.state || '',
            Country: formData.country || '',
            PostalCode: formData.postalCode || '',
            MakaniNo: formData.makaniNumber || '',
            MunicipalityNo: formData.municipalityNo || '',
            ReraPropertyNo: formData.reraPropertyNo || '',
            Latitude: formData.latitude || 0,
            Longitude: formData.longitude || 0,
            ManagerName: formData.buildingManager || '',
            MobileNo: formData.contactNumber || '',
            Telephone: '',
            Email: formData.email || '',
            MaintenanceCompany: formData.maintenanceCompany || '',
            SecurityCompany: formData.securityCompany || '',
            // pending payload 
            // OthersIncomeAccount: formData.othersIncome || '',
            // OthersPrepaidAccount: formData.othersPrepaid || '',
            RentalIncomeAccount: formData.rentIncome || '',
            RentalIncomeAccountPP: formData.rentPrepaid || '',
            MaintenanceIncomeAccount: formData.serviceIncome || '',
            MaintenanceIncomeAccountPP: formData.servicePrepaid || '',
            EjariIncomeAccount: formData.ejariIncome || '',
            EjariIncomeAccountPP: formData.ejariPrepaid || '',
            CommissionIncomeAccount: formData.commissionIncome || '',
            CommissionIncomeAccountPP: formData.commissionPrepaid || '',
            ParkingIncomeAccount: formData.parkingIncome || '',
            ParkingIncomeAccountPP: formData.parkingPrepaid || '',
            DepositAccount: formData.depositAcc || '',
            ExpenseAccount: formData.expenseAcc || '',
            SettlementAccount: formData.settlementAcc || '',
            CashAccount: formData.cashAcc || '',
            PDCAccount: formData.pdcAcc || '',
            VATOutputAccount: formData.vatOutputAcc || '',
            VATInputAccount: formData.vatInputAcc || '',
            CostCenter: formData.costCenter || '',
            ElectricityProvider: formData.electricityProvider || '',
            WaterProvider: formData.waterProvider || '',
            GasProvider: formData.gasProvider || '',
            InsuranceCompany: formData.insuranceDetails || '',
            PolicyNumber: formData.policyNumber || '',
            PolicyExpiryDate: now,
            HasGym: formData.hasGym || false,
            HasSwimmingPool: formData.hasSwimmingPool || false,
            HasKidsPlayArea: formData.hasKidsPlayArea || false,
            HasCCTV: formData.hasCCTV || false,
            HasCentralAC: formData.hasCentralAC || false,
            HasFireAlarm: formData.hasFireAlarm || false,
            HasFireFighting: formData.hasFireFighting || false,
            HasGenerator: formData.hasGenerator || false,
            HasVisitorParking: formData.hasVisitorParking || false,
            Remarks: formData.notes || '',
            CompanyCode: companyCode,
            deptno: department,
            IsActive: true,
            CreatedBy: isEditing ? (formData.createdBy || 'admin') : 'admin',
            CreatedDate: isEditing ? (formData.createdDate || now) : now,
            ModifiedBy: isEditing ? (formData.createdBy || 'admin') : 'admin',
            ModifiedDate: now
        }];

        console.log('Building Save Payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await axios.post(`${apiPaths[0]?.API_CONFIG}/api/BuildingMaster`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            const result = response.data;
            console.log('Save Response:', result);

            showSnackbar(`Building ${isEditing ? 'updated' : 'registered'} successfully!`, "success");

            if (isEditing) {
                setBuildings(prev => prev.map(b => b.build_key === formData.build_key ? formData : b));
            } else {
                if (buildings.some(b => b.build_key === formData.build_key)) {
                    showSnackbar("Building Code already exists.", "error");
                    return;
                }
                setBuildings(prev => [...prev, formData]);
            }
            handleCreateNew();
        } catch (error) {
            console.error('Error saving building:', error);
            showSnackbar('Failed to save building: ' + (error.response?.data?.message || error.message), "error");
        }
    };

    const handleDelete = (key) => {
        setConfirmDelete(key);
    };

    const filteredBuildings = buildings.filter(b =>
        b.build_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.build_key.toLowerCase().includes(searchQuery.toLowerCase())
    );


    const handleCustomerSelect = (data) => {
        console.log("data:", data);
        if (activeModalField) {
            const setterMap = {
                depositAcc: setDepositAccount,
                settlementAcc: setSettlementAccount,
                cashAcc: setCashAccount,
                pdcAcc: setPdcAccount,
                vatOutputAcc: setVatOutputAccount,
                vatInputAcc: setVatInputAccount,
                ejariAcc: setEjariAccount,
                commissionAcc: setCommissionAccount,
                rentIncome: setRentIncomeAccount,
                rentPrepaid: setRentPrepaidAccount,
                serviceIncome: setServiceIncomeAccount,
                servicePrepaid: setServicePrepaidAccount,
                ejariIncome: setEjariIncomeAccount,
                ejariPrepaid: setEjariPrepaidAccount,
                commissionIncome: setCommissionIncomeAccount,
                commissionPrepaid: setCommissionPrepaidAccount,
                parkingIncome: setParkingIncomeAccount,
                parkingPrepaid: setParkingPrepaidAccount,
                othersIncome: setOthersIncomeAccount,
                othersPrepaid: setOthersPrepaidAccount,
            };
            const modalCloserMap = {
                depositAcc: setDepositModalOpen,
                settlementAcc: setSettlementModalOpen,
                cashAcc: setCashModalOpen,
                pdcAcc: setPdcModalOpen,
                vatOutputAcc: setVatOutputModalOpen,
                vatInputAcc: setVatInputModalOpen,
                ejariAcc: setEjariModalOpen,
                commissionAcc: setCommissionModalOpen,
                rentIncome: setRentIncomeModalOpen,
                rentPrepaid: setRentPrepaidModalOpen,
                serviceIncome: setServiceIncomeModalOpen,
                servicePrepaid: setServicePrepaidModalOpen,
                ejariIncome: setEjariIncomeModalOpen,
                ejariPrepaid: setEjariPrepaidModalOpen,
                commissionIncome: setCommissionIncomeModalOpen,
                commissionPrepaid: setCommissionPrepaidModalOpen,
                parkingIncome: setParkingIncomeModalOpen,
                parkingPrepaid: setParkingPrepaidModalOpen,
                othersIncome: setOthersIncomeModalOpen,
                othersPrepaid: setOthersPrepaidModalOpen,
            };
            setterMap[activeModalField](data.ACCOUNT);
            setFormData((prev) => ({ ...prev, [activeModalField]: data.ACCOUNT }));
            modalCloserMap[activeModalField](false);
        }
        setActiveModalField(null);
    };
    return (
        <div className=" bg-slate-50/50 p-4 md:p-8 text-slate-800 font-sans">

            {/* Title & Top Bar Actions */}
            <div className=" mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Building Register</h1>
                    {/* <p className="text-sm text-slate-500">Configure core operational property data matrices and financial account routing</p> */}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={handleCreateNew}
                        className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                    >
                        Clear Fields
                    </button>
                    <button
                        type="submit"
                        onClick={handleSave}
                        className="px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-all shadow-sm"
                    >
                        {isEditing ? "Apply Changes" : "Register Building"}
                    </button>
                </div>
            </div>

            <div className=" mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Left/Middle Column: Workspace Master Configuration Panels */}
                <div className="lg:col-span-2 space-y-6 max-h-[92vh] overflow-auto">
                    <Card className="!p-0 overflow-hidden">
                        {/* Dynamic Segmented Tab Header Control */}
                        <div className="flex border-b border-slate-200 bg-slate-50">
                            <button
                                onClick={() => setActiveTab('building_info')}
                                className={`flex-1 py-3.5 px-6 font-medium text-sm border-b-2 transition-all flex items-center justify-center gap-2 ${activeTab === 'building_info'
                                    ? 'border-blue-600 text-blue-600 bg-white font-semibold'
                                    : 'border-transparent text-slate-500 hover:text-slate-800'
                                    }`}
                            >
                                Building Information
                            </button>
                            <button
                                onClick={() => setActiveTab('account_setting')}
                                className={`flex-1 py-3.5 px-6 font-medium text-sm border-b-2 transition-all flex items-center justify-center gap-2 ${activeTab === 'account_setting'
                                    ? 'border-blue-600 text-blue-600 bg-white font-semibold'
                                    : 'border-transparent text-slate-500 hover:text-slate-800'
                                    }`}
                            >
                                Account Settings
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-6">

                            {/* TOP ROW: Images + Location */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                {/* Building Images */}
                                <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <ImageIcon size={12} />
                                            Building Images
                                        </span>
                                    </div>

                                    {imageData && imageData.length > 0 ? (
                                        <div className="rounded-xl overflow-hidden">
                                            <div className="flex items-center justify-center h-40 mb-3 rounded bg-white overflow-hidden">
                                                <img
                                                    src={getImageUrl(imageData[currentImageIndex]?.imagePath)}
                                                    alt={`Building ${currentImageIndex + 1}`}
                                                    className="object-contain h-full"
                                                    key={currentImageIndex}
                                                    onError={(e) => {
                                                        console.error("Image load error");
                                                        console.log("Failed URL:", getImageUrl(imageData[currentImageIndex]?.imagePath));
                                                    }}
                                                />
                                            </div>
                                            <div className="text-center text-xs text-slate-500 mb-2">
                                                Image {currentImageIndex + 1} of {imageData.length}
                                            </div>
                                        </div>
                                    ) : uploadingImages ? (
                                        <div className="w-full h-40 rounded-xl border border-dashed border-blue-300 bg-blue-50/50 flex flex-col items-center justify-center gap-2">
                                            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                                            <span className="text-xs text-blue-500 font-medium">Uploading image...</span>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleAddImage}
                                            disabled={!formData.build_key || uploadingImages}
                                            className={`w-full h-40 rounded-xl border border-dashed text-xs transition-colors ${!formData.build_key || uploadingImages ? 'border-slate-200 text-slate-300 cursor-not-allowed' : 'border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-500'}`}
                                        >
                                            No images yet — click to upload
                                        </button>
                                    )}

                                    <div className="flex justify-between items-center mt-3">
                                        {imageData?.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={handlePrev}
                                                className="p-2 bg-slate-200 hover:bg-slate-300 rounded-md shadow"
                                            >
                                                <svg className="w-4 h-4 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleAddImage}
                                            disabled={!formData.build_key || uploadingImages}
                                            className={`px-4 py-2 text-white rounded transition flex justify-center items-center gap-2 text-xs font-semibold ${!formData.build_key || uploadingImages ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-700'}`}
                                        >
                                            {uploadingImages ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                                <Plus className="w-3 h-3" />
                                            )}
                                            {uploadingImages ? 'Uploading...' : 'Add'}
                                        </button>

                                        {imageData?.length > 0 && !uploadingImages && (
                                            <button
                                                type="button"
                                                onClick={handleRemoveItem}
                                                className="px-4 py-2 text-white rounded transition flex justify-center items-center gap-2 text-xs font-semibold bg-red-500 hover:bg-red-700"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                                Remove
                                            </button>
                                        )}

                                        {imageData?.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={handleNext}
                                                className="p-2 bg-slate-200 hover:bg-slate-300 rounded-md shadow"
                                            >
                                                <svg className="w-4 h-4 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                            </button>
                                        )}
                                    </div>

                                    <p className="mt-2 text-[10px] text-slate-400 text-center">
                                        Only <span className="font-medium">JPG</span> and{" "}
                                        <span className="font-medium">PNG</span> formats are allowed!
                                    </p>
                                </div>

                                {/* Location */}
                                <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                            <MapPin size={12} />
                                            Location
                                        </span>
                                        {loc && (
                                            <button
                                                type="button"
                                                onClick={clearLocation}
                                                className="text-xs font-semibold text-slate-400 hover:text-rose-500 flex items-center gap-1"
                                            >
                                                <Trash2 size={12} />
                                                Clear
                                            </button>
                                        )}
                                    </div>

                                    {/* Address search */}
                                    <div className="flex gap-1.5">
                                        <input
                                            type="text"
                                            value={addressInput}
                                            onChange={(e) => setAddressInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    searchAddress();
                                                }
                                            }}
                                            placeholder="Type an address..."
                                            className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500"
                                        />
                                        <button
                                            type="button"
                                            onClick={searchAddress}
                                            disabled={geocoding || !addressInput.trim()}
                                            className="shrink-0 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-2.5 flex items-center justify-center transition-colors disabled:opacity-50"
                                            title="Find address"
                                        >
                                            <Search size={14} />
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2 my-2">
                                        <span className="h-px flex-1 bg-slate-200" />
                                        <span className="text-[10px] font-semibold text-slate-400 uppercase">or</span>
                                        <span className="h-px flex-1 bg-slate-200" />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={useCurrentLocation}
                                        disabled={locating}
                                        className="w-full rounded-lg border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-600 text-slate-500 text-xs font-semibold py-2 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                                    >
                                        <LocateFixed size={13} />
                                        {locating ? "Locating..." : "Use current location"}
                                    </button>

                                    {locationError && (
                                        <p className="text-[11px] text-rose-500 mt-1">{locationError}</p>
                                    )}

                                    {loc ? (
                                        <div className="space-y-2 mt-2">
                                            <div className="w-full h-24 rounded-lg overflow-hidden border border-slate-200">
                                                <iframe
                                                    title="location-preview"
                                                    src={mapEmbedUrl}
                                                    className="w-full h-full pointer-events-none"
                                                    loading="lazy"
                                                />
                                            </div>
                                            {loc.address && (
                                                <p className="text-[11px] text-slate-600 truncate" title={loc.address}>
                                                    {loc.address}
                                                </p>
                                            )}
                                            <p className="text-[11px] font-mono text-slate-400 truncate">
                                                {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
                                            </p>
                                            <a
                                                href={googleMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-full rounded-lg border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-600 text-slate-500 text-xs font-semibold py-2 flex items-center justify-center gap-1.5 transition-colors"
                                            >
                                                <ExternalLink size={13} />
                                                Open in Google Maps
                                            </a>
                                        </div>
                                    ) : (
                                        <div className="mt-2 grid grid-cols-2 gap-2">
                                            <Input label="Latitude" id="latitude" value={formData.latitude} onChange={handleInputChange} placeholder="e.g., 25.2048" />
                                            <Input label="Longitude" id="longitude" value={formData.longitude} onChange={handleInputChange} placeholder="e.g., 55.2708" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* TAB 1: BUILDING INFO */}
                            {activeTab === 'building_info' && (
                                <div className="space-y-6 animate-fadeIn">
                                    {/* Section 1: Basic Information */}
                                    <div>
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Basic Information</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input label="Building Code" id="build_key" value={formData.build_key} onChange={handleInputChange} placeholder="e.g., B001" disabled={isEditing} required />
                                            <Input label="Building Name" id="build_name" value={formData.build_name} onChange={handleInputChange} placeholder="e.g., Al Manarah" required />
                                            <Select label="Property Type" id="propertyType" value={formData.propertyType} onChange={handleInputChange}
                                                options={[
                                                    { value: "Residential", label: "Residential" },
                                                    { value: "Commercial", label: "Commercial" },
                                                    { value: "Mixed Use", label: "Mixed Use" },
                                                    { value: "Warehouse", label: "Warehouse" },
                                                    { value: "Villa", label: "Villa" }
                                                ]}
                                            />
                                            <Input label="Project/Community" id="projectCommunity" value={formData.projectCommunity} onChange={handleInputChange} placeholder="Community or project name" />
                                            <Input label="Owner" id="ownerName" value={formData.ownerName} onChange={handleInputChange} placeholder="Building owner/company" />
                                            <Input label="Developer" id="developer" value={formData.developer} onChange={handleInputChange} placeholder="Developer name" />
                                        </div>
                                    </div>

                                    {/* Section 2: Building Specifications */}
                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/60 space-y-4">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Building Specifications</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input label="Number of Floors" id="numFloors" type="number" value={formData.numFloors} onChange={handleInputChange} placeholder="Total floors" />
                                            <Input label="Number of Units" id="numUnits" type="number" value={formData.numUnits} onChange={handleInputChange} placeholder="Total apartments/offices/shops" />
                                            <Input label="Parking Spaces" id="numParking" type="number" value={formData.numParking} onChange={handleInputChange} placeholder="Total parking slots" />
                                            <Input label="Number of Elevators" id="numElevators" type="number" value={formData.numElevators} onChange={handleInputChange} placeholder="Total lifts" />
                                            <Input label="Year Built" id="yearBuilt" type="number" value={formData.yearBuilt} onChange={handleInputChange} placeholder="e.g., 2020" />
                                            <Select label="Status" id="status" value={formData.status} onChange={handleInputChange}
                                                options={[
                                                    { value: "Active", label: "Active" },
                                                    { value: "Under Construction", label: "Under Construction" },
                                                    { value: "Sold", label: "Sold" },
                                                    { value: "Closed", label: "Closed" }
                                                ]}
                                            />
                                            <Select label="Building Category" id="buildingCategory" value={formData.buildingCategory} onChange={handleInputChange}
                                                options={[
                                                    { value: "Freehold", label: "Freehold" },
                                                    { value: "Leasehold", label: "Leasehold" }
                                                ]}
                                            />
                                        </div>
                                    </div>

                                    {/* Section 3: Address Information */}
                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/60 space-y-4">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Address</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="md:col-span-2">
                                                <Input label="Address" id="address" value={formData.address} onChange={handleInputChange} placeholder="Full address" />
                                            </div>
                                            <Input label="City" id="city" value={formData.city} onChange={handleInputChange} placeholder="City" />
                                            <Input label="State/Province" id="state" value={formData.state} onChange={handleInputChange} placeholder="State/Province" />
                                            <Input label="Country" id="country" value={formData.country} onChange={handleInputChange} placeholder="Country" />
                                            <Input label="Postal Code" id="postalCode" value={formData.postalCode} onChange={handleInputChange} placeholder="ZIP/Postal code" />
                                        </div>
                                    </div>

                                    {/* Section 4: Registration & Plot Info */}
                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/60 space-y-4">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Registration & Land Information</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Input label="Municipality No" id="municipalityNo" value={formData.municipalityNo} onChange={handleInputChange} placeholder="Municipality registration number" />
                                            <Input label="Makani Number (UAE)" id="makaniNumber" value={formData.makaniNumber} onChange={handleInputChange} placeholder="Dubai Makani Number" />
                                            <Input label="Plot Number" id="plotNo" value={formData.plotNo} onChange={handleInputChange} placeholder="Plot/Land number" />
                                            <Input label="Land Area (sq ft)" id="landArea" type="number" value={formData.landArea} onChange={handleInputChange} placeholder="Plot size" />
                                            <Input label="Built-up Area (sq ft)" id="builtUpArea" type="number" value={formData.builtUpArea} onChange={handleInputChange} placeholder="Total built-up area" />
                                            <Input label="Total Rental Area (sq ft)" id="totalRentalArea" type="number" value={formData.totalRentalArea} onChange={handleInputChange} placeholder="Rentable area" />
                                            <Input label="Common Area (sq ft)" id="commonArea" type="number" value={formData.commonArea} onChange={handleInputChange} placeholder="Common area size" />
                                        </div>
                                    </div>

                                    {/* Section 5: Facilities & Management */}
                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/60 space-y-4">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Facilities & Management</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                            <Input label="Maintenance Company" id="maintenanceCompany" value={formData.maintenanceCompany} onChange={handleInputChange} placeholder="Service provider" />
                                            <Input label="Security Company" id="securityCompany" value={formData.securityCompany} onChange={handleInputChange} placeholder="Security contractor" />
                                            <Input label="Insurance Details" id="insuranceDetails" value={formData.insuranceDetails} onChange={handleInputChange} placeholder="Policy information" />
                                            <Input
                                                label="Electricity Provider"
                                                id="electricityProvider"
                                                value={formData.electricityProvider}
                                                onChange={handleInputChange}
                                                placeholder="e.g. DEWA"
                                            />

                                            <Input
                                                label="Water Provider"
                                                id="waterProvider"
                                                value={formData.waterProvider}
                                                onChange={handleInputChange}
                                                placeholder="e.g. DEWA"
                                            />

                                            <Input
                                                label="Gas Provider"
                                                id="gasProvider"
                                                value={formData.gasProvider}
                                                onChange={handleInputChange}
                                                placeholder="e.g. Emirates Gas"
                                            />
                                            <Input label="Building Manager" id="buildingManager" value={formData.buildingManager} onChange={handleInputChange} placeholder="Property manager" />
                                            <Input label="Contact Number" id="contactNumber" value={formData.contactNumber} onChange={handleInputChange} placeholder="Manager phone" />
                                            <Input label="Email" id="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="Building email" />
                                            <div>
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                                    Facilities
                                                </label>

                                                <div className="grid grid-cols-3 gap-4">

                                                    {[
                                                        { label: "Gym", key: "hasGym" },
                                                        { label: "Swimming Pool", key: "hasSwimmingPool" },
                                                        { label: "Kids Play Area", key: "hasKidsPlayArea" },
                                                        { label: "CCTV", key: "hasCCTV" },
                                                        { label: "Central AC", key: "hasCentralAC" },
                                                        { label: "Fire Alarm", key: "hasFireAlarm" },
                                                        { label: "Fire Fighting", key: "hasFireFighting" },
                                                        { label: "Generator", key: "hasGenerator" },
                                                        { label: "Visitor Parking", key: "hasVisitorParking" },
                                                    ].map((item) => (
                                                        <label
                                                            key={item.key}
                                                            className="flex items-center gap-2 cursor-pointer group"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={formData[item.key]}
                                                                onChange={(e) =>
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        [item.key]: e.target.checked,
                                                                    }))
                                                                }
                                                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                            />

                                                            <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                                                                {item.label}
                                                            </span>
                                                        </label>
                                                    ))}

                                                </div>
                                            </div>
                                        </div>
                                    </div>



                                    {/* Sub Section Container: Landlord Details */}
                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/60 space-y-4">
                                        {/* <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Landlord Reference Matrix</h4> */}
                                        <div className="grid grid-cols-1  gap-4">
                                            <Input label="Landlord Legal Name" id="landlord" value={formData.landlord} onChange={handleInputChange} />
                                            {/* <Input label="Landlord Phone" id="landlordPhone" value={formData.landlordPhone} onChange={handleInputChange} placeholder="+971" />
                                            <div className="md:col-span-2">
                                                <Input label="Landlord Corporate Address" id="landlordAddress" value={formData.landlordAddress} onChange={handleInputChange} />
                                            </div>
                                            <div className="md:col-span-2">
                                                <Input label="Landlord Business E-mail" id="landlordEmail" type="email" value={formData.landlordEmail} onChange={handleInputChange} placeholder="name@domain.com" />
                                            </div> */}
                                        </div>
                                    </div>

                                    {/* Section 6: Notes */}
                                    <div>
                                        <Input label="Notes" id="notes" value={formData.notes} onChange={handleInputChange} placeholder="Remarks" />
                                    </div>
                                </div>
                            )}

                            {/* TAB 2: ACCOUNT SETTINGS ROUTING */}
                            {activeTab === 'account_setting' && (
                                <div className="space-y-6 animate-fadeIn">

                                    {/* Primary General Ledger Mappings */}
                                    <div>
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Primary Accounts Settings</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                                            {/* Deposit A/C */}
                                            <Tooltip title={formData.depositAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Deposit A/C</label>

                                                    <input
                                                        id="depositAcc"
                                                        value={formData.depositAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("depositAcc");
                                                                fetchAccountList("ALL");
                                                                setDepositModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.depositAcc && (
                                                        <button type="button" onClick={() => clearAccountField("depositAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip>

                                            {/* Settlement A/C */}
                                            <Tooltip title={formData.settlementAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Settlement A/C</label>
                                                    <input
                                                        id="settlementAcc"
                                                        value={formData.settlementAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("settlementAcc");
                                                                fetchAccountList("ALL");
                                                                setSettlementModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.settlementAcc && (
                                                        <button type="button" onClick={() => clearAccountField("settlementAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip>

                                            {/* Cash A/C */}
                                            <Tooltip title={formData.cashAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Cash A/C</label>
                                                    <input
                                                        id="cashAcc"
                                                        value={formData.cashAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("cashAcc");
                                                                fetchAccountList("ALL");
                                                                setCashModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.cashAcc && (
                                                        <button type="button" onClick={() => clearAccountField("cashAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip>

                                            {/* PDC A/C */}
                                            <Tooltip title={formData.pdcAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">PDC A/C</label>
                                                    <input
                                                        id="pdcAcc"
                                                        value={formData.pdcAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("pdcAcc");
                                                                fetchAccountList("ALL");
                                                                setPdcModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.pdcAcc && (
                                                        <button type="button" onClick={() => clearAccountField("pdcAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip>

                                            {/* VAT Output A/C */}
                                            <Tooltip title={formData.vatOutputAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">VAT Output A/C</label>
                                                    <input
                                                        id="vatOutputAcc"
                                                        value={formData.vatOutputAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("vatOutputAcc");
                                                                fetchAccountList("ALL");
                                                                setVatOutputModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.vatOutputAcc && (
                                                        <button type="button" onClick={() => clearAccountField("vatOutputAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip>

                                            {/* VAT Input A/C */}
                                            <Tooltip title={formData.vatInputAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">VAT Input A/C</label>
                                                    <input
                                                        id="vatInputAcc"
                                                        value={formData.vatInputAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("vatInputAcc");
                                                                fetchAccountList("ALL");
                                                                setVatInputModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.vatInputAcc && (
                                                        <button type="button" onClick={() => clearAccountField("vatInputAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip>

                                            {/* Commission A/C */}
                                            {/* <Tooltip title={formData.commissionAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Commission A/C</label>
                                                    <input
                                                        id="commissionAcc"
                                                        value={formData.commissionAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("commissionAcc");
                                                                fetchAccountList("ALL");
                                                                setCommissionModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.commissionAcc && (
                                                        <button type="button" onClick={() => clearAccountField("commissionAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip> */}

                                            {/* Ejari A/C */}
                                            {/* <Tooltip title={formData.ejariAcc ? "" : "Press F2 to select account"} arrow>
                                                <div className="flex flex-col items-left gap-1.5">
                                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Ejari A/C</label>
                                                    <input
                                                        id="ejariAcc"
                                                        value={formData.ejariAcc || ""}
                                                        readOnly
                                                        placeholder="Press F2"
                                                        onKeyDown={(e) => {
                                                            if (e.key === "F2") {
                                                                e.preventDefault();
                                                                setActiveModalField("ejariAcc");
                                                                fetchAccountList("ALL");
                                                                setEjariModalOpen(true);
                                                            }
                                                        }}
                                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                    />
                                                    {formData.ejariAcc && (
                                                        <button type="button" onClick={() => clearAccountField("ejariAcc")} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </Tooltip> */}

                                            {/* <div className="flex flex-col items-left gap-1.5">
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Ejari Fixed Amt (AED)</label>
                                                <Input id="ejariFixedAmt" type="number" value={formData.ejariFixedAmt} onChange={handleInputChange} placeholder="Amount" />
                                            </div> */}



                                        </div>
                                    </div>

                                    <hr className="border-slate-200" />

                                    {/* Operational Ledger Income Allocation Columns */}
                                    <div>
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Income Account Settings</h4>
                                        <div className="space-y-3">
                                            {/* Grid Row Header */}
                                            <div className="hidden sm:grid grid-cols-3 gap-4 text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">
                                                <div>Description</div>
                                                <div>Income A/C</div>
                                                <div>Prepaid A/C</div>
                                            </div>

                                            {[
                                                { label: "Rent", incId: "rentIncome", prepId: "rentPrepaid" },
                                                { label: "Service", incId: "serviceIncome", prepId: "servicePrepaid" },
                                                { label: "Ejari", incId: "ejariIncome", prepId: "ejariPrepaid" },
                                                { label: "Commission", incId: "commissionIncome", prepId: "commissionPrepaid" },
                                                { label: "Parking", incId: "parkingIncome", prepId: "parkingPrepaid" },
                                                // { label: "Others", incId: "othersIncome", prepId: "othersPrepaid" }
                                            ].map((row, idx) => (
                                                <div key={idx} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center bg-slate-50/50 p-2 sm:p-0 rounded-lg sm:bg-transparent">
                                                    <span className="text-xs font-medium text-slate-700 sm:pl-1">{row.label}</span>

                                                    {/* Income A/C - F2 Modal */}
                                                    <Tooltip title={formData[row.incId] ? "" : "Press F2 to select account"} arrow>
                                                        <div className="flex flex-col items-left gap-1.5">
                                                            <input
                                                                id={row.incId}
                                                                value={formData[row.incId] || ""}
                                                                readOnly
                                                                placeholder="Press F2"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "F2") {
                                                                        e.preventDefault();
                                                                        setActiveModalField(row.incId);
                                                                        fetchAccountList("ALL");
                                                                        switch (row.incId) {
                                                                            case "rentIncome": setRentIncomeModalOpen(true); break;
                                                                            case "serviceIncome": setServiceIncomeModalOpen(true); break;
                                                                            case "ejariIncome": setEjariIncomeModalOpen(true); break;
                                                                            case "commissionIncome": setCommissionIncomeModalOpen(true); break;
                                                                            case "parkingIncome": setParkingIncomeModalOpen(true); break;
                                                                            case "othersIncome": setOthersIncomeModalOpen(true); break;
                                                                        }
                                                                    }
                                                                }}
                                                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                            />
                                                            {formData[row.incId] && (
                                                                <button type="button" onClick={() => clearAccountField(row.incId)} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                                    <Trash2 size={13} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </Tooltip>

                                                    {/* Prepaid A/C - F2 Modal */}
                                                    <Tooltip title={formData[row.prepId] ? "" : "Press F2 to select account"} arrow>
                                                        <div className="flex flex-col items-left gap-1.5">
                                                            <input
                                                                id={row.prepId}
                                                                value={formData[row.prepId] || ""}
                                                                readOnly
                                                                placeholder="Press F2"
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "F2") {
                                                                        e.preventDefault();
                                                                        setActiveModalField(row.prepId);
                                                                        fetchAccountList("ALL");
                                                                        switch (row.prepId) {
                                                                            case "rentPrepaid": setRentPrepaidModalOpen(true); break;
                                                                            case "servicePrepaid": setServicePrepaidModalOpen(true); break;
                                                                            case "ejariPrepaid": setEjariPrepaidModalOpen(true); break;
                                                                            case "commissionPrepaid": setCommissionPrepaidModalOpen(true); break;
                                                                            case "parkingPrepaid": setParkingPrepaidModalOpen(true); break;
                                                                            case "othersPrepaid": setOthersPrepaidModalOpen(true); break;
                                                                        }
                                                                    }
                                                                }}
                                                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                                                            />
                                                            {formData[row.prepId] && (
                                                                <button type="button" onClick={() => clearAccountField(row.prepId)} className="shrink-0 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Remove account">
                                                                    <Trash2 size={13} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <hr className="border-slate-200" />



                                </div>
                            )}
                        </form>
                    </Card>
                </div>

                {/* Right Column: Database Grid Real-Time Overview Record Feed */}
                <div className="lg:col-span-1 space-y-4 max-h-[92vh] overflow-auto ">
                    <Card className="!p-4 flex flex-col  ">
                        <div className="mb-3">
                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-2">Registered Building</h3>
                            <p className="text-xs text-slate-400">Search and select active building records below to initialize direct adjustments.</p>
                        </div>

                        {/* Search Sub-component */}
                        <div className="relative mb-3">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            </span>
                            <input
                                type="text"
                                placeholder="Search by code or name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-slate-100 border-none rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                        </div>

                        {/* Scrollable Mini-Cards instead of an unreadable wide data table */}
                        <div className=" p-2 max-h-auto overflow-auto">

                            <div className="overflow-y-auto flex-1 space-y-2.5 p-1">
                                {loadingBuildings ? (
                                    <div className="text-center py-8 text-xs text-slate-400">Loading buildings...</div>
                                ) : filteredBuildings.length === 0 ? (
                                    <div className="text-center py-8 text-xs text-slate-400 ">No matching buildings found.</div>
                                ) : (
                                    filteredBuildings.map((building) => (
                                        <div
                                            key={building.build_key}
                                            onClick={() => handleSelectRow(building)}
                                            className={` p-3.5 rounded-xl border transition-all cursor-pointer relative group ${formData.build_key === building.build_key
                                                ? 'bg-blue-50/70 border-blue-300 ring-1 ring-blue-300'
                                                : 'bg-white border-slate-200 hover:border-slate-300 shadow-2xs'
                                                }`}
                                        >


                                            <div className="flex justify-between items-start mb-1">
                                                <div>
                                                    <span className="text-[10px] font-bold tracking-widest text-blue-600 uppercase bg-blue-100/60 px-1.5 py-0.5 rounded">
                                                        {building.build_key}
                                                    </span>
                                                    <span className="ml-2 text-[10px] font-bold tracking-widest text-green-600 uppercase bg-green-100/60 px-1.5 py-0.5 rounded">
                                                        {building.status}
                                                    </span>
                                                    <h4 className="text-sm font-semibold text-slate-800 mt-1">{building.build_name}</h4>
                                                </div>

                                                {/* Inline Record Erasure */}
                                                {/* <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleDelete(building.build_key); }}
                                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 p-1 rounded transition-all"
                                                title="Delete record"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button> */}
                                                {buildingThumbnails[building.build_key] && (
                                                    <div className="w-14 h-14 mb-2 rounded-lg overflow-hidden bg-slate-100">
                                                        <img
                                                            src={buildingThumbnails[building.build_key]}
                                                            alt={building.build_name}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-2 gap-y-1.5 gap-x-2 pt-2 mt-2 border-t border-slate-100 text-[11px] text-slate-500">
                                                <div><strong className="text-slate-400 font-normal">Plot:</strong> {building.plotNo || 'N/A'}</div>
                                                <div><strong className="text-slate-400 font-normal">Type:</strong> {building.propertyType || 'N/A'}</div>
                                                <div><strong className="text-slate-400 font-normal">Location:</strong> {building.city || building.location || 'N/A'}</div>
                                                <div><strong className="text-slate-400 font-normal">Units:</strong> {building.numUnits || 'N/A'}</div>
                                                {/* <div><strong className="text-slate-400 font-normal">Status:</strong> {building.status || 'N/A'}</div> */}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                    </Card>
                </div>

            </div>

            {/* MUI Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {depositModalOpen && (
                <Modal isOpen={depositModalOpen} onClose={() => setDepositModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {settlementModalOpen && (
                <Modal isOpen={settlementModalOpen} onClose={() => setSettlementModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {cashModalOpen && (
                <Modal isOpen={cashModalOpen} onClose={() => setCashModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {pdcModalOpen && (
                <Modal isOpen={pdcModalOpen} onClose={() => setPdcModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {vatOutputModalOpen && (
                <Modal isOpen={vatOutputModalOpen} onClose={() => setVatOutputModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {vatInputModalOpen && (
                <Modal isOpen={vatInputModalOpen} onClose={() => setVatInputModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {ejariModalOpen && (
                <Modal isOpen={ejariModalOpen} onClose={() => setEjariModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {commissionModalOpen && (
                <Modal isOpen={commissionModalOpen} onClose={() => setCommissionModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}

            {/* Income Account Modals */}
            {rentIncomeModalOpen && (
                <Modal isOpen={rentIncomeModalOpen} onClose={() => setRentIncomeModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {rentPrepaidModalOpen && (
                <Modal isOpen={rentPrepaidModalOpen} onClose={() => setRentPrepaidModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {serviceIncomeModalOpen && (
                <Modal isOpen={serviceIncomeModalOpen} onClose={() => setServiceIncomeModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {servicePrepaidModalOpen && (
                <Modal isOpen={servicePrepaidModalOpen} onClose={() => setServicePrepaidModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {ejariIncomeModalOpen && (
                <Modal isOpen={ejariIncomeModalOpen} onClose={() => setEjariIncomeModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {ejariPrepaidModalOpen && (
                <Modal isOpen={ejariPrepaidModalOpen} onClose={() => setEjariPrepaidModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {commissionIncomeModalOpen && (
                <Modal isOpen={commissionIncomeModalOpen} onClose={() => setCommissionIncomeModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {commissionPrepaidModalOpen && (
                <Modal isOpen={commissionPrepaidModalOpen} onClose={() => setCommissionPrepaidModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {parkingIncomeModalOpen && (
                <Modal isOpen={parkingIncomeModalOpen} onClose={() => setParkingIncomeModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {parkingPrepaidModalOpen && (
                <Modal isOpen={parkingPrepaidModalOpen} onClose={() => setParkingPrepaidModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {othersIncomeModalOpen && (
                <Modal isOpen={othersIncomeModalOpen} onClose={() => setOthersIncomeModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
            {othersPrepaidModalOpen && (
                <Modal isOpen={othersPrepaidModalOpen} onClose={() => setOthersPrepaidModalOpen(false)} title="Ledger List" size="xxl" header={true}>
                    <CreditCustomerList defaultType="All" handleCustomerSelect={handleCustomerSelect} />
                </Modal>
            )}
        </div>
    );
}